import { ArrowUpRight, BarChart3, Gauge } from 'lucide-react';
import { Link } from '@tanstack/react-router';

import { ShigoPermission } from '../../../auth/permissions';
import { useShigoPermissions } from '../../../auth/useShigoPermissions';
import type { ReportExplorerRouteSearch } from '../../../reporting/reportExplorer/reportExplorerRouteSearch';
import type { PerformanceAnalyzerRouteSearch } from '../../../reporting/performanceAnalyzer/performanceAnalyzerRouteSearch';
import { dashboardT } from '../../../../lib/messages';
import { DashboardWidgetAction } from '../../model';
import { WidgetActionButton } from './WidgetActionButton';

interface DashboardWidgetActionsProps {
  actions: readonly DashboardWidgetAction[];
  className?: string | undefined;
  /** Icon-only outlined buttons with tooltips, for table headers inside a card. */
  compact?: boolean | undefined;
  reportingSearch?: ReportExplorerRouteSearch | null | undefined;
  performanceAnalyzerSearch?: PerformanceAnalyzerRouteSearch | null | undefined;
}

export function DashboardWidgetActions({
  actions,
  className,
  compact,
  reportingSearch,
  performanceAnalyzerSearch,
}: DashboardWidgetActionsProps) {
  const hasReportingAction =
    actions.includes(DashboardWidgetAction.ViewInReporting) && Boolean(reportingSearch);
  const hasPerformanceAnalyzerAction =
    actions.includes(DashboardWidgetAction.PerformanceAnalyzer) &&
    Boolean(performanceAnalyzerSearch);

  if (!hasReportingAction && !hasPerformanceAnalyzerAction) return null;

  return (
    <AuthorizedDashboardWidgetActions
      actions={actions}
      className={className}
      compact={compact}
      reportingSearch={reportingSearch}
      performanceAnalyzerSearch={performanceAnalyzerSearch}
    />
  );
}

function AuthorizedDashboardWidgetActions({
  actions,
  className,
  compact,
  reportingSearch,
  performanceAnalyzerSearch,
}: DashboardWidgetActionsProps) {
  const { can } = useShigoPermissions();
  const showReporting =
    actions.includes(DashboardWidgetAction.ViewInReporting) &&
    Boolean(reportingSearch) &&
    can(ShigoPermission.ReportingReportBuilderRead);
  const showPerformanceAnalyzer =
    actions.includes(DashboardWidgetAction.PerformanceAnalyzer) &&
    Boolean(performanceAnalyzerSearch) &&
    can(ShigoPermission.ReportingPerformanceRead);
  if (!showReporting && !showPerformanceAnalyzer) return null;

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-1">
        {showPerformanceAnalyzer && performanceAnalyzerSearch ? (
          <WidgetActionButton
            compact={compact}
            label={dashboardT('performanceAnalyzer.title')}
            icon={<Gauge className="h-4 w-4" aria-hidden="true" />}
            renderAction={(content, ariaLabel) => (
              <Link
                to="/reporting/performance-analyzer"
                search={performanceAnalyzerSearch}
                aria-label={ariaLabel}
              >
                {content}
              </Link>
            )}
          />
        ) : null}
        {showReporting && reportingSearch ? (
          <WidgetActionButton
            compact={compact}
            label={dashboardT('dashboard.reporting')}
            icon={
              compact ? (
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              ) : (
                <BarChart3 className="h-4 w-4" aria-hidden="true" />
              )
            }
            renderAction={(content, ariaLabel) => (
              <Link to="/reporting/report-explorer" search={reportingSearch} aria-label={ariaLabel}>
                {content}
              </Link>
            )}
          />
        ) : null}
      </div>
    </div>
  );
}
